import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import AppSectionLoader from '@/components/AppSectionLoader';
import InsightsCard from '@features/statistics/components/InsightsCard';
import { getMenuData } from '@/core/data/menuData';
import { apiClient } from '@core/api';
import { useNotifications } from '@/core/hooks/useNotifications';
import { useAuthStore, useApiStore } from '@core/store';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';

interface WanzlGatesPerformance {
    name: string;
    openingsMainDirection: number;
}

interface InsightsData {
    storesActivated: number;
    gatesOperational: number;
    performanceLast7Days: WanzlGatesPerformance[];
    performanceLast30Days: WanzlGatesPerformance[];
    alarmsLast7Days: number;
    alarmsLast30Days: number;
}

const GatesInsights: FCSecurePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifyErrorResponse } = useNotifications();
    const { user } = useAuthStore();
    const { shopIds } = useApiStore();
    const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const loadData = useCallback(async () => {
        setDataLoading(true);
        try {
            const response = await apiClient.get<InsightsData>('statistics/wanzlGates/insights');
            setInsightsData(response.data);
            setLastUpdate(DateTime.now());
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setDataLoading(false);
        }
    }, [notifyErrorResponse]);

    useEffect(() => {
        loadData();
    }, []);

    useRegisterRefresh(loadData);

    const navigateToOpenings = (days: number) => {
        const allIds = shopIds.map((s) => s.id).join(',');
        const end = DateTime.utc().toISO() ?? '';
        const start = DateTime.utc().minus({ days }).toISO() ?? '';
        navigate(`/statistics/gates/openings?shopIds=${allIds}&start=${start}&end=${end}`);
    };

    const orgName = user?.organisation?.name ?? '';

    const allDeviceIds = useMemo(() => {
        if (!insightsData) return [];
        const ids = [
            ...insightsData.performanceLast7Days.map((p) => p.name),
            ...insightsData.performanceLast30Days.map((p) => p.name)
        ];
        return [...new Set(ids)];
    }, [insightsData]);

    const rows7Days = useMemo(() => {
        if (!insightsData) return [];
        return [
            ...allDeviceIds.map((deviceId) => ({
                label: `${deviceId} ${t('openings')}`,
                value: insightsData.performanceLast7Days.find((p) => p.name === deviceId)?.openingsMainDirection ?? 0
            })),
            { label: t('alarmByManipulation'), value: insightsData.alarmsLast7Days }
        ];
    }, [insightsData, allDeviceIds, t]);

    const rows30Days = useMemo(() => {
        if (!insightsData) return [];
        return [
            ...allDeviceIds.map((deviceId) => ({
                label: `${deviceId} ${t('openings')}`,
                value: insightsData.performanceLast30Days.find((p) => p.name === deviceId)?.openingsMainDirection ?? 0
            })),
            { label: t('alarmByManipulation'), value: insightsData.alarmsLast30Days }
        ];
    }, [insightsData, allDeviceIds, t]);

    return (
        <div>
            <NewPageTitleBar
                icon={<i className="bi bi-clipboard-data" />}
                title={t('insights')}
                iconSize="20px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/gates/insights' }}
                lastUpdate={lastUpdate}
                t={t}
            />
            <div className="container grid-list-xl no-padding-top container--fluid">
                <AppSectionLoader status={dataLoading} />
                <div className="row">
                    <InsightsCard
                        heading={t('installedBaseOf', { orgName })}
                        rows={[
                            { label: t('storesActivated'), value: insightsData?.storesActivated ?? '–' },
                            { label: t('gatesOperational'), value: insightsData?.gatesOperational ?? '–' }
                        ]}
                        onClick={() => navigate('/myshops')}
                    />
                    <InsightsCard
                        heading={t('performanceLast7Days')}
                        rows={rows7Days}
                        onClick={() => navigateToOpenings(7)}
                    />
                    <InsightsCard
                        heading={t('performanceLast30Days')}
                        rows={rows30Days}
                        onClick={() => navigateToOpenings(30)}
                    />
                </div>
            </div>
        </div>
    );
};

export default GatesInsights;
